import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import apiService from '../services/api';
import { getFieldId, deduplicateFeatures, hasFeatureWithId } from '../utils/fieldUtils';

import SprayWeatherWidget from './SprayWeatherWidget';
interface MapViewProps {
  fieldsData: any; // User's own fields from CVR selection
  selectedFields: string[]; // Fields selected for current action (Step 3 order list) - now CVR-Marknr format
  onFieldClick: (fieldId: string, fieldData?: any) => void;
  enableZoomLoading?: boolean; // Whether to enable zoom-based field loading (default: true)
  externalZoomFields?: any[]; // Pre-loaded zoom fields from parent (for showing across pages)
  highlightedZoomFields?: string[]; // Zoom field IDs from MyFields to highlight (light yellow when not selected)
}

// Default center: Denmark
const DENMARK_CENTER: [number, number] = [56.0, 10.0];
const DEFAULT_ZOOM = 13;
const DEFAULT_EMPTY_ZOOM = 7; // Zoom level when no fields are loaded (shows Denmark overview)

/**
 * Calculate the center point of GeoJSON features.
 * Returns [lat, lon] or null if no valid features.
 */
const calculateCenter = (geojsonData: any): [number, number] | null => {
  if (!geojsonData?.features || geojsonData.features.length === 0) {
    return null;
  }

  let minLat = Infinity, maxLat = -Infinity;
  let minLon = Infinity, maxLon = -Infinity;

  for (const feature of geojsonData.features) {
    if (!feature.geometry?.coordinates) continue;

    // Handle Polygon and MultiPolygon
    const coords = feature.geometry.type === 'MultiPolygon'
      ? feature.geometry.coordinates.flat(2)
      : feature.geometry.coordinates[0];

    if (!coords) continue;

    for (const coord of coords) {
      const lon = coord[0];
      const lat = coord[1];
      if (typeof lat === 'number' && typeof lon === 'number') {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
      }
    }
  }

  if (minLat === Infinity || maxLat === -Infinity) {
    return null;
  }

  const centerLat = (minLat + maxLat) / 2;
  const centerLon = (minLon + maxLon) / 2;

  return [centerLat, centerLon];
};

// Component to handle map bounds when data loads - centers and zooms to show all fields
const MapBounds: React.FC<{ geojsonData: any; defaultZoom?: number }> = ({
  geojsonData,
  defaultZoom = DEFAULT_ZOOM
}) => {
  const map = useMap();

  useEffect(() => {
    if (geojsonData && geojsonData.features && geojsonData.features.length > 0) {
      const bounds = new (window as any).L.geoJSON(geojsonData).getBounds();
      // Fit bounds with padding, but don't zoom out too far
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: defaultZoom  // Don't zoom in more than default
      });
    }
  }, [geojsonData, map, defaultZoom]);

  return null;
};

// Component to reset map to Denmark view when fields are cleared
const MapReset: React.FC<{ shouldReset: boolean }> = ({ shouldReset }) => {
  const map = useMap();

  useEffect(() => {
    if (shouldReset) {
      map.setView(DENMARK_CENTER, DEFAULT_EMPTY_ZOOM);
    }
  }, [shouldReset, map]);

  return null;
};

// Component to handle zoom-based field loading
const ZoomFieldLoader: React.FC<{
  onFieldsLoaded: (fields: any) => void;
  minZoom: number;
}> = ({ onFieldsLoaded, minZoom }) => {
  const map = useMap();
  const [loading, setLoading] = useState(false);

  const loadFieldsInBounds = useCallback(async () => {
    const zoom = map.getZoom();

    // Only load additional fields when zoomed in enough
    if (zoom < minZoom) {
      onFieldsLoaded(null);
      return;
    }

    const bounds = map.getBounds();

    setLoading(true);
    try {
      const data = await apiService.getFieldsByBounds({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
      onFieldsLoaded(data);
    } catch (error) {
      console.error('Error loading fields:', error);
      onFieldsLoaded(null);
    } finally {
      setLoading(false);
    }
  }, [map, minZoom, onFieldsLoaded]);

  useMapEvents({
    zoomend: () => {
      loadFieldsInBounds();
    },
    moveend: () => {
      const zoom = map.getZoom();
      if (zoom >= minZoom) {
        loadFieldsInBounds();
      }
    }
  });

  // Load on mount if already zoomed in
  useEffect(() => {
    const zoom = map.getZoom();
    if (zoom >= minZoom) {
      loadFieldsInBounds();
    }
  }, [map, minZoom, loadFieldsInBounds]);

  return loading ? (
    <div className="leaflet-top leaflet-right" style={{ marginTop: '10px', marginRight: '10px' }}>
      <div className="bg-white px-3 py-2 rounded shadow-lg text-sm flex items-center gap-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
        <span>Henter marker...</span>
      </div>
    </div>
  ) : null;
};

const MapView: React.FC<MapViewProps> = ({
  fieldsData,
  selectedFields,
  onFieldClick,
  enableZoomLoading = true,
  externalZoomFields = [],
  highlightedZoomFields = []
}) => {
  const [ownFieldsData, setOwnFieldsData] = useState<any>(null);
  const [zoomedFieldsData, setZoomedFieldsData] = useState<any>(null);
  const [combinedData, setCombinedData] = useState<any>(null);

  // Calculate initial center from fieldsData
  const initialCenter = useMemo(() => {
    const center = calculateCenter(fieldsData);
    return center || DENMARK_CENTER;
  }, [fieldsData]);

  // Calculate initial zoom - use empty zoom when no data
  const initialZoom = useMemo(() => {
    const hasFields = fieldsData?.features && fieldsData.features.length > 0;
    return hasFields ? DEFAULT_ZOOM : DEFAULT_EMPTY_ZOOM;
  }, [fieldsData]);

  // FIX: Update own fields when fieldsData changes (including null)
  useEffect(() => {
    setOwnFieldsData(fieldsData);
  }, [fieldsData]);

  // Combine own fields + zoomed fields + external zoom fields with FULL deduplication
  useEffect(() => {
    const rawOwnFeatures = ownFieldsData?.features || [];
    const rawZoomedFeatures = zoomedFieldsData?.features || [];
    const rawExternalFeatures = externalZoomFields || [];

    // STEP 1: Deduplicate each source within itself using CVR-Marknr
    const ownFeatures = deduplicateFeatures(rawOwnFeatures);
    const zoomedFeatures = deduplicateFeatures(rawZoomedFeatures);
    const externalFeatures = deduplicateFeatures(rawExternalFeatures);

    // STEP 2: Build set of own field IDs (CVR-Marknr)
    const ownFieldIds = new Set<string>();
    ownFeatures.forEach((f: any) => {
      const id = getFieldId(f);
      if (id) ownFieldIds.add(id);
    });

    // STEP 3: Filter zoomed fields - remove any that exist in own
    const uniqueZoomedFeatures = zoomedFeatures.filter(
      (f: any) => !ownFieldIds.has(getFieldId(f))
    );

    // STEP 4: Build combined set of IDs (own + zoomed)
    const combinedIds = new Set<string>();
    ownFeatures.forEach((f: any) => {
      const id = getFieldId(f);
      if (id) combinedIds.add(id);
    });
    uniqueZoomedFeatures.forEach((f: any) => {
      const id = getFieldId(f);
      if (id) combinedIds.add(id);
    });

    // STEP 5: Filter external fields - remove any that exist in own or zoomed
    const uniqueExternalFields = externalFeatures.filter(
      (f: any) => !combinedIds.has(getFieldId(f))
    );

    // STEP 6: Combine all unique features
    const allFeatures = ownFeatures.concat(uniqueZoomedFeatures).concat(uniqueExternalFields);

    if (allFeatures.length > 0) {
      setCombinedData({
        type: 'FeatureCollection',
        features: allFeatures
      });
    } else {
      setCombinedData(null);
    }
  }, [ownFieldsData, zoomedFieldsData, externalZoomFields]);

  const handleZoomedFieldsLoaded = useCallback((data: any) => {
    setZoomedFieldsData(data);
  }, []);

  const onEachFeature = (feature: any, layer: any) => {
    const fieldId = getFieldId(feature);  // CVR-Marknr
    const markblokId = feature.properties.MARKBLOKNR;  // For display
    const marknr = feature.properties.Marknr;
    const areal = feature.properties.GB_AREAL || feature.properties.IMK_areal || feature.properties.GEOMETRISK;
    const cvr = feature.properties.CVR;

    // Is this field selected in the current selection (Step 3 order list)?
    const isSelected = selectedFields.includes(fieldId);

    // Is this a CVR field (user's own)?
    const isCvrField = hasFeatureWithId(ownFieldsData?.features || [], fieldId);

    // Is this a zoom field from MyFields (highlighted)?
    const isHighlightedZoom = highlightedZoomFields.includes(fieldId);

    // Is this in externalZoomFields?
    const isExternalZoomField = hasFeatureWithId(externalZoomFields, fieldId);

    /*
     * COLOR SCHEME (6 states):
     *
     * | Field Type           | NOT selected | SELECTED      |
     * |----------------------|--------------|---------------|
     * | CVR (user's own)     | Blue         | Green         |
     * | MyFields Zoom        | Light Yellow | Bold Yellow   |
     * | Other Zoom           | Grey         | Blue          |
     *
     * MyFields Zoom = isExternalZoomField AND isHighlightedZoom
     * Other Zoom = NOT isCvrField AND NOT (isExternalZoomField AND isHighlightedZoom)
     */

    let fillColor: string;
    let strokeColor: string;

    // Determine if this is a "MyFields zoom" field (from externalZoomFields AND highlighted)
    const isMyFieldsZoom = isExternalZoomField && isHighlightedZoom;

    if (isCvrField) {
      // CVR field (user's own)
      fillColor = '#4783b6';
      strokeColor = '#4783b6';
    } else if (isMyFieldsZoom) {
      // Zoom field from MyFields (saved non-CVR)
      fillColor = '#a0532a';
      strokeColor = '#a0532a';
    } else {
      // Other zoom field (new, not from MyFields)
      if (isSelected) {
        fillColor = '#a0532a'; // Selected non-CVR
        strokeColor = '#a0532a';
      } else {
        fillColor = '#7b7b7b'; // Non-CVR grey
        strokeColor = '#7b7b7b';
      }
    }

    layer.setStyle({
      fillColor: fillColor,
      fillOpacity: isSelected ? 0.9 : 0.3,
      color: strokeColor,
      weight: isSelected ? 3 : 2,
    });

    // Popup content with field type indicator
    let fieldTypeLabel = '';
    if (isCvrField) {
      fieldTypeLabel = '<em style="color: #22c55e;">Din mark</em>';
    } else if (isMyFieldsZoom) {
      fieldTypeLabel = '<em style="color: #eab308;">Fra Mine Marker</em>';
    } else {
      fieldTypeLabel = '<em style="color: #64748b;">Andre marker</em>';
    }

    const popupContent = `
      <div class="p-2">
        <strong>${marknr || 'Ingen marknr'}</strong><br/>
        Markblok: ${markblokId}<br/>
        Areal: ${areal ? areal.toFixed(2) : 'N/A'} ha<br/>
        CVR: ${cvr || 'N/A'}<br/>
        ${fieldTypeLabel}
      </div>
    `;

    layer.bindPopup(popupContent);

    // Tooltip on hover
    layer.bindTooltip(
      `<strong>${marknr || 'Ingen marknr'}</strong><br/>${areal ? areal.toFixed(2) : 'N/A'} ha`,
      {
        permanent: false,
        sticky: true,
        direction: 'top',
        className: 'bg-white border border-gray-300 rounded shadow-lg p-2 text-sm'
      }
    );

    // Click handler - send field ID (CVR-Marknr) and feature data to parent
    layer.on({
      click: (e: any) => {
        // Prevent popup from opening when clicking to select
        if (e.originalEvent.shiftKey) {
          e.target.closePopup();
        }
        // Pass CVR-Marknr field ID and full feature to parent
        onFieldClick(fieldId, feature);
      },
      mouseover: (e: any) => {
        if (!isSelected) {
          e.target.setStyle({
            fillOpacity: isMyFieldsZoom ? 0.7 : 0.5,
            weight: 3,
          });
        }
      },
      mouseout: (e: any) => {
        if (!isSelected) {
          e.target.setStyle({
            fillOpacity: isMyFieldsZoom ? 0.5 : 0.3,
            weight: 2,
          });
        }
      },
    });
  };

  // Determine if we should reset map to Denmark view
  const shouldResetToDenmark = !ownFieldsData ||
    !ownFieldsData.features ||
    ownFieldsData.features.length === 0;

  // FIX: Always render MapContainer - show Denmark map even when no fields
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
    <MapContainer
      center={initialCenter}
      zoom={initialZoom}
      style={{ height: '100%', width: '100%' }}
      className="z-0"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/* Reset to Denmark view when all fields are removed */}
      <MapReset shouldReset={shouldResetToDenmark} />

      {/* Load additional fields when zoomed in - only if enabled */}
      {enableZoomLoading && (
        <ZoomFieldLoader
          onFieldsLoaded={handleZoomedFieldsLoaded}
          minZoom={14}
        />
      )}

      {combinedData && (
        <>
          <GeoJSON
            data={combinedData}
            onEachFeature={onEachFeature}
            key={`geojson-${combinedData.features.length}-${selectedFields.join(',')}-${highlightedZoomFields.join(',')}`}
          />
          <MapBounds geojsonData={ownFieldsData} defaultZoom={DEFAULT_ZOOM} />
        </>
      )}

      {/* Show hint when no fields are loaded */}
      {shouldResetToDenmark && (
        <div className="leaflet-bottom leaflet-left" style={{ marginBottom: '20px', marginLeft: '10px' }}>
          <div className="bg-white px-4 py-3 rounded shadow-lg text-sm max-w-xs">
            <p className="text-gray-600">
              <strong>Tip:</strong> Vælg CVR'er i panelet til venstre for at se dine marker på kortet.
            </p>
          </div>
        </div>
      )}
    </MapContainer>
        <SprayWeatherWidget />
    </div>
  );
};

export default MapView;
